import React, { useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { RequestViewButton } from "../shared/button/Buttons";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { message } from "antd";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { 
  CopyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

interface TestSetAddedRequestsTableProps {
  requests: ChatCompletionRequest[];
  apiKey: ApiKeyOrGetter;
  title?: string;
  showHeader?: boolean;
  className?: string;
  selectable?: boolean; // New prop to enable/disable selection
  selectedRequests?: ChatCompletionRequest[]; // Selected requests for checkbox state
  onSelectionChange?: (requests: ChatCompletionRequest[]) => void; // Callback for selection changes
  showOnlySelected?: boolean; // New prop to show only selected requests
}

const TestSetAddedRequestsTable: React.FC<TestSetAddedRequestsTableProps> = ({
  requests,
  apiKey,
  title = "Selected Requests",
  showHeader = true,
  className,
  selectable = false, // Default to false for backward compatibility
  selectedRequests = [], // Default to empty array
  onSelectionChange,
  showOnlySelected = false // Default to false
}) => {
  const [expandedTagRows, setExpandedTagRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Get selected row keys for table selection
  const selectedRowKeys = selectedRequests.map(r => r.request_id);

  const handleRowSelection = (requestId: string, isSelected: boolean) => {
    if (!onSelectionChange) return;
    
    const request = displayedRequests.find(r => r.request_id === requestId);
    if (!request) return;

    if (isSelected) {
      onSelectionChange([...selectedRequests, request]);
    } else {
      const newSelectedRequests = selectedRequests.filter(row => row.request_id !== requestId);
      onSelectionChange(newSelectedRequests);
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (!onSelectionChange) return;

    if (isSelected) {
      // Add all current page requests to selection
      const newRequests = displayedRequests.filter(request => !selectedRowKeys.includes(request.request_id));
      onSelectionChange([...selectedRequests, ...newRequests]);
    } else {
      // Remove all current page requests from selection
      const currentPageIds = displayedRequests.map(r => r.request_id);
      const newSelectedRequests = selectedRequests.filter(
        row => !currentPageIds.includes(row.request_id)
      );
      onSelectionChange(newSelectedRequests);
    }
  };

  const formatDate = (timestamp: number) => {
    return moment(timestamp).format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    return moment(timestamp).format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success("Request ID copied to clipboard!");
    }).catch(() => {
      message.error("Failed to copy request ID.");
    });
  };

  const renderRequestId = (requestId: string) => {
    // Show last 8 characters with ellipsis prefix for better readability
    const truncatedId = `...${requestId.slice(-8)}`;
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {truncatedId}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(requestId);
          }}
        >
          <CopyIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  const toggleTagExpansion = (requestId: string) => {
    const newExpanded = new Set(expandedTagRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedTagRows(newExpanded);
  };

  const renderTagsCell = (record: ChatCompletionRequest) => {
    let tags: string[] = [];

    if (record.metadata?._maitai_test_request_meta) {
      try {
        const meta = JSON.parse(record.metadata._maitai_test_request_meta as string);
        tags = meta.tags || [];
      } catch (e) {
        console.error("Error parsing metadata:", e);
      }
    }

    if (tags.length === 0) {
      return <span className="text-sm text-muted-foreground">No tags</span>;
    }

    const isExpanded = expandedTagRows.has(record.request_id);
    const maxTagsToShow = 3;
    const visibleTags = isExpanded ? tags : tags.slice(0, maxTagsToShow);
    const hasMoreTags = tags.length > maxTagsToShow;

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {visibleTags.map((tag, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {hasMoreTags && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                toggleTagExpansion(record.request_id);
              }}
            >
              {isExpanded ? (
                <>
                  <ChevronUpIcon className="w-3 h-3 mr-1" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="w-3 h-3 mr-1" />
                  +{tags.length - maxTagsToShow} more
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Filter requests based on showOnlySelected
  const displayedRequests = showOnlySelected ? selectedRequests : requests;

  // Calculate if all current page items are selected
  const currentPageStart = (currentPage - 1) * pageSize;
  const currentPageEnd = currentPage * pageSize;
  const currentPageRequests = displayedRequests.slice(currentPageStart, currentPageEnd);
  const currentPageIds = currentPageRequests.map(r => r.request_id);
  const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedRowKeys.includes(id));

  // Define columns for PaginatedTable
  const columns: PaginatedTableColumn<ChatCompletionRequest>[] = [
    ...(selectable ? [{
      key: "selection" as keyof ChatCompletionRequest,
      header: (
        <Checkbox
          checked={allCurrentPageSelected}
          onCheckedChange={handleSelectAll}
        />
      ),
      width: "w-[50px]",
      render: (record: ChatCompletionRequest) => {
        const isSelected = selectedRowKeys.includes(record.request_id);
        return (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => {
              handleRowSelection(record.request_id, !!checked);
            }}
          />
        );
      }
    }] : []),
    {
      key: "date_created" as keyof ChatCompletionRequest,
      header: "Date Created",
      width: selectable ? "w-[18%]" : "w-[20%]",
      render: (record: ChatCompletionRequest) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(record.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(record.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "request_id" as keyof ChatCompletionRequest,
      header: "Request ID",
      width: selectable ? "w-[18%]" : "w-[20%]",
      render: (record: ChatCompletionRequest) => renderRequestId(record.request_id)
    },
    {
      key: "metadata" as keyof ChatCompletionRequest,
      header: "Tags",
      width: selectable ? "w-[32%]" : "w-[45%]",
      render: (record: ChatCompletionRequest) => (
        <div onClick={(e) => e.stopPropagation()}>
          {renderTagsCell(record)}
        </div>
      )
    },
    {
      key: "actions" as keyof ChatCompletionRequest,
      header: "Actions",
      width: "w-[15%]",
      render: (record: ChatCompletionRequest) => (
        <div className="flex items-center gap-1">
          <RequestViewButton
            buttonStyle={{ color: "#21b892" }}
            apiKey={apiKey}
            request={record}
            to={`/application/${record.application_id}/session/${encodeURIComponent(record.session_id)}/request/${record.request_id}`}
          />
        </div>
      )
    }
  ];

  if (!requests || requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="space-y-3">
          <h3 className="text-lg font-gc-med text-foreground">
            No requests selected
          </h3>
          <p className="text-muted-foreground max-w-md">
            Select requests from the eligible requests table to see them here.
          </p>
        </div>
      </div>
    );
  }

  if (!showHeader) {
    return (
      <div className={cn("space-y-4", className)}>
        <PaginatedTable
          data={displayedRequests}
          columns={columns}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          emptyTitle="No requests selected"
          emptyDescription="Select requests from the eligible requests table to see them here."
          rowClassName={(record) => {
            const isSelected = selectedRowKeys.includes(record.request_id);
            return cn(
              "hover:bg-accent/20 transition-colors",
              isSelected && "bg-maitai-green/5"
            );
          }}
          showPagination={displayedRequests.length > pageSize}
        />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <CheckCircleIcon className="w-5 h-5 text-maitai-green" />
            {title} ({displayedRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <PaginatedTable
            data={displayedRequests}
            columns={columns}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            emptyTitle="No requests selected"
            emptyDescription="Select requests from the eligible requests table to see them here."
            rowClassName={(record) => {
              const isSelected = selectedRowKeys.includes(record.request_id);
              return cn(
                "hover:bg-accent/20 transition-colors",
                isSelected && "bg-maitai-green/5"
              );
            }}
            showPagination={displayedRequests.length > pageSize}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default TestSetAddedRequestsTable; 